/**
 * Document Inbox — Wave F.
 *
 * Lightweight evidence store: receipts, vendor invoices, signed contracts,
 * statements — anything the controller wants to attach to a journal entry,
 * AP bill, or stay unattached in the inbox for triage.
 *
 * Beta-safe: no upload, no OCR. Documents are metadata records (filename,
 * mime, size, capturedAt, source) referenced by `attachTo()`. When the
 * project gains Cloud storage, only the storage layer changes — this
 * context's API is the seam.
 *
 * Status lifecycle:
 *   inbox   → freshly added, not attached
 *   linked  → attached to ≥1 entity (bill / je / vendor / job)
 *   archived→ deliberately filed away without linking
 */
import {
  createContext, useCallback, useContext, useMemo, useState,
} from "react";

export type DocumentSource = "email_forward" | "manual_upload" | "vendor_portal" | "scanner" | "drive_sync";
export type DocumentStatus = "inbox" | "linked" | "archived";
export type DocumentEntityType = "bill" | "journal_entry" | "vendor" | "customer" | "job" | "fixed_asset";

export interface DocumentLink {
  entityType: DocumentEntityType;
  entityId: string;
  linkedAt: string;
  linkedBy: string;
}

export interface DocumentRecord {
  id: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  source: DocumentSource;
  capturedAt: string;
  capturedBy: string;
  /** Optional vendor pulled from email subject / OCR — pre-fills attach dialog. */
  detectedVendorName?: string;
  /** Optional amount detected (cents not enforced; user confirms before posting). */
  detectedAmount?: number;
  detectedDate?: string;
  notes?: string;
  status: DocumentStatus;
  links: DocumentLink[];
  archivedAt?: string;
  archivedReason?: string;
}

interface NewDocumentInput {
  filename: string;
  mimeType: string;
  sizeBytes: number;
  source: DocumentSource;
  capturedBy?: string;
  detectedVendorName?: string;
  detectedAmount?: number;
  detectedDate?: string;
  notes?: string;
}

interface DocumentInboxContextValue {
  documents: DocumentRecord[];
  addDocument: (input: NewDocumentInput) => DocumentRecord;
  attachTo: (documentId: string, entityType: DocumentEntityType, entityId: string) => void;
  detachFrom: (documentId: string, entityType: DocumentEntityType, entityId: string) => void;
  archive: (documentId: string, reason: string) => void;
  restore: (documentId: string) => void;
  getDocumentsFor: (entityType: DocumentEntityType, entityId: string) => DocumentRecord[];
  getInboxCount: () => number;
}

const Ctx = createContext<DocumentInboxContextValue | null>(null);

export function DocumentInboxProvider({ children }: { children: React.ReactNode }) {
  const [documents, setDocuments] = useState<DocumentRecord[]>([]);

  const addDocument = useCallback((input: NewDocumentInput): DocumentRecord => {
    if (!input.filename.trim()) throw new Error("Filename is required.");
    if (input.sizeBytes < 0) throw new Error("Size must be ≥ 0.");
    const doc: DocumentRecord = {
      id: `doc_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      filename: input.filename.trim(),
      mimeType: input.mimeType,
      sizeBytes: input.sizeBytes,
      source: input.source,
      capturedAt: new Date().toISOString(),
      capturedBy: input.capturedBy ?? "Steve",
      detectedVendorName: input.detectedVendorName,
      detectedAmount: input.detectedAmount,
      detectedDate: input.detectedDate,
      notes: input.notes,
      status: "inbox",
      links: [],
    };
    setDocuments(prev => [doc, ...prev]);
    return doc;
  }, []);

  const attachTo = useCallback((docId: string, entityType: DocumentEntityType, entityId: string) => {
    setDocuments(prev => prev.map(d => {
      if (d.id !== docId) return d;
      if (d.links.some(l => l.entityType === entityType && l.entityId === entityId)) return d;
      return {
        ...d,
        status: "linked",
        links: [...d.links, {
          entityType, entityId,
          linkedAt: new Date().toISOString(),
          linkedBy: "Steve",
        }],
      };
    }));
  }, []);

  const detachFrom = useCallback((docId: string, entityType: DocumentEntityType, entityId: string) => {
    setDocuments(prev => prev.map(d => {
      if (d.id !== docId) return d;
      const links = d.links.filter(l => !(l.entityType === entityType && l.entityId === entityId));
      return {
        ...d,
        links,
        status: links.length === 0 && d.status === "linked" ? "inbox" : d.status,
      };
    }));
  }, []);

  const archive = useCallback((docId: string, reason: string) => {
    setDocuments(prev => prev.map(d => d.id === docId
      ? { ...d, status: "archived", archivedAt: new Date().toISOString(), archivedReason: reason }
      : d));
  }, []);

  const restore = useCallback((docId: string) => {
    setDocuments(prev => prev.map(d => d.id === docId
      ? { ...d, status: d.links.length > 0 ? "linked" : "inbox",
          archivedAt: undefined, archivedReason: undefined }
      : d));
  }, []);

  const getDocumentsFor = useCallback((entityType: DocumentEntityType, entityId: string) => {
    return documents.filter(d => d.links.some(l => l.entityType === entityType && l.entityId === entityId));
  }, [documents]);

  const getInboxCount = useCallback(() => documents.filter(d => d.status === "inbox").length, [documents]);

  const value = useMemo<DocumentInboxContextValue>(() => ({
    documents, addDocument, attachTo, detachFrom, archive, restore,
    getDocumentsFor, getInboxCount,
  }), [documents, addDocument, attachTo, detachFrom, archive, restore, getDocumentsFor, getInboxCount]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useDocumentInbox() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useDocumentInbox must be used inside <DocumentInboxProvider>");
  return ctx;
}
